import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import {
  ArrowLeft, Building2, CalendarDays, ChevronDown, ChevronRight, ClipboardEdit, Filter, History,
  Loader2, Plus, RefreshCw, Save, Search, SlidersHorizontal, Trash2, TrendingUp, UserCircle,
} from 'lucide-react';
import { toast } from 'sonner';
import { apiFetch } from '@/lib/supabaseClient';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { hasCap } from '@/lib/capabilities';
import { useEffectiveMe } from '@/lib/effectiveMe';
import BalanceExplanation from '@/components/ferie/BalanceExplanation';

const MESI = ['Gen', 'Feb', 'Mar', 'Apr', 'Mag', 'Giu', 'Lug', 'Ago', 'Set', 'Ott', 'Nov', 'Dic'];
export const STATI = [
  ['richiesta', 'Richiesta'], ['approvata', 'Approvata'], ['programmata', 'Programmata'],
  ['goduta', 'Goduta'], ['rifiutata', 'Rifiutata'], ['annullata', 'Annullata'],
];
const EMPTY_FILTERS = { anni: [], aziende: [], locali: [], reparti: [], dipendenti: [], stati: STATI.map(([value]) => value) };

export function fmt(value) {
  return Number(value || 0).toLocaleString('it-IT', { maximumFractionDigits: 2 });
}

export function stateLabel(value) {
  return STATI.find(([key]) => key === value)?.[1] || value || '—';
}

function stateTone(value) {
  return {
    richiesta: 'bg-amber-50 text-amber-800 border-amber-200',
    approvata: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    programmata: 'bg-blue-50 text-blue-800 border-blue-200',
    goduta: 'bg-stone-100 text-stone-700 border-stone-200',
    rifiutata: 'bg-red-50 text-red-700 border-red-200',
    annullata: 'bg-stone-100 text-stone-500 border-stone-200 line-through',
  }[value] || 'bg-stone-50 text-stone-600 border-stone-200';
}

export function StateBadge({ value }) {
  return <span className={`inline-flex rounded-full border px-2 py-1 text-[10px] font-bold uppercase ${stateTone(value)}`}>{stateLabel(value)}</span>;
}

export function Metric({ label, value, tone = 'default', hint, highlight = false, displayLabel = label, unit = 'g' }) {
  const color = tone === 'good' ? 'text-brand-green' : tone === 'warn' ? 'text-amber-700' : tone === 'info' ? 'text-brand-blue' : 'text-brand-navy';
  return (
    <div className={`rounded-2xl border p-4 ${highlight ? 'border-brand-green bg-emerald-50/70 shadow-sm ring-1 ring-emerald-100' : 'border-app-border bg-app-surface'}`} data-testid={`kpi-${label.toLowerCase().replaceAll(' ', '-')}`}>
      <div className={`font-display ${highlight ? 'text-3xl' : 'text-2xl'} font-semibold ${color}`}>{fmt(value)}{unit && <span className="ml-1 text-sm font-normal text-stone-500">{unit}</span>}</div>
      <div className="mt-1 text-[10px] font-bold uppercase tracking-widest text-stone-500">{displayLabel}</div>
      {hint && <div className="mt-1 text-[11px] text-stone-400">{hint}</div>}
    </div>
  );
}

function SelectFilter({ id, label, value, onChange, options, disabled = false }) {
  return (
    <div className="min-w-0">
      <label htmlFor={id} className="sr-only">{label}</label>
      <select
        id={id}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        disabled={disabled}
        data-testid={id}
        className="h-10 w-full min-w-0 rounded-xl border border-app-border-strong bg-app-surface px-3 text-sm text-app-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-200 disabled:cursor-not-allowed disabled:opacity-50"
      >
        <option value="all">{label}</option>
        {options.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
      </select>
    </div>
  );
}

function ActiveFilters({ filters, values }) {
  const labels = [
    ['year', 'Anno', String(values.year)],
    ['company', 'Azienda', filters.aziende.find((item) => item.id === values.company)?.nome],
    ['location', 'Locale', filters.locali.find((item) => item.id === values.location)?.nome],
    ['department', 'Reparto', filters.reparti.find((item) => item.id === values.department)?.nome],
    ['employee', 'Dipendente', filters.dipendenti.find((item) => item.id === values.employee)?.displayName],
    ['state', 'Stato', STATI.find(([key]) => key === values.state)?.[1]],
  ].filter(([, , label]) => label);
  return (
    <div className="mt-3 flex flex-wrap items-center gap-1.5" data-testid="ferie-analisi-active-filters">
      <span className="mr-1 text-[10px] font-bold uppercase tracking-widest text-stone-400">Attivi</span>
      {labels.length === 0 && <span className="text-xs text-stone-500">Tutto il perimetro autorizzato</span>}
      {labels.map(([key, title, label]) => <span key={key} className="inline-flex max-w-full items-center rounded-full border border-emerald-100 bg-emerald-50 px-2.5 py-1 text-xs text-emerald-800"><span className="mr-1 font-semibold">{title}:</span><span className="truncate">{label}</span></span>)}
    </div>
  );
}

export function YearNotice({ meta }) {
  if (!meta) return null;
  const styles = meta.tipo === 'previsione'
    ? 'border-blue-200 bg-blue-50 text-blue-900'
    : meta.tipo === 'non_registrato'
      ? 'border-amber-200 bg-amber-50 text-amber-900'
      : 'border-stone-200 bg-stone-50 text-stone-700';
  return (
    <div className={`flex items-start gap-2 rounded-xl border px-3 py-2.5 text-xs ${styles}`} role="status" data-testid="ferie-analisi-year-status">
      <CalendarDays className="mt-0.5 h-4 w-4 shrink-0" />
      <div><span className="font-semibold">{meta.etichetta}</span><span className="ml-1">{meta.messaggio}</span></div>
    </div>
  );
}

export function FilterPanel({ filters, values, setValues, loading }) {
  const [open, setOpen] = useState(false);
  const locations = useMemo(() => (
    filters.locali
      .filter((item) => values.company === 'all' || item.aziendaId === values.company)
      .map((item) => ({ value: item.id, label: item.nome }))
  ), [filters.locali, values.company]);
  const departments = useMemo(() => (
    filters.reparti
      .filter((item) => values.location === 'all' || item.localeId === values.location)
      .map((item) => ({ value: item.id, label: item.nome }))
  ), [filters.reparti, values.location]);
  const employees = useMemo(() => (
    filters.dipendenti
      .filter((item) => values.company === 'all' || item.aziendaId === values.company)
      .filter((item) => values.location === 'all' || item.localeId === values.location)
      .filter((item) => values.department === 'all' || item.repartoId === values.department)
      .map((item) => ({ value: item.id, label: item.displayName || `${item.nome} ${item.cognome}` }))
  ), [filters.dipendenti, values.company, values.location, values.department]);
  const set = (key, value) => setValues((current) => ({ ...current, [key]: value }));
  const clear = () => setValues((current) => ({ ...current, company: 'all', location: 'all', department: 'all', employee: 'all', state: 'all' }));

  return (
    <section className="rounded-2xl border border-app-border bg-app-surface p-3 sm:p-4" role="search" aria-label="Filtri analisi ferie">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-sm font-semibold text-brand-navy">
          <SlidersHorizontal className="h-4 w-4 text-brand-green" /> Perimetro e filtri
        </div>
        <div className="flex items-center gap-1">
          <Button type="button" variant="ghost" size="sm" onClick={clear} className="text-brand-blue" data-testid="ferie-analisi-clear">Azzera</Button>
          <Button type="button" variant="ghost" size="sm" onClick={() => setOpen((current) => !current)} className="gap-1 text-stone-700" aria-expanded={open} data-testid="ferie-analisi-toggle-filters">
            {open ? 'Chiudi' : 'Apri'} <ChevronDown className={`h-4 w-4 transition-transform ${open ? 'rotate-180' : ''}`} />
          </Button>
        </div>
      </div>
      <ActiveFilters filters={filters} values={values} />
      <div className={open ? 'mt-3 block' : 'hidden'}>
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-2 sm:gap-3 lg:grid-cols-6">
        <SelectFilter id="ferie-analisi-year" label="Anno" value={String(values.year)} onChange={(value) => set('year', Number(value))} options={filters.anni.map((year) => ({ value: year, label: String(year) }))} disabled={loading} />
        <SelectFilter id="ferie-analisi-company" label="Tutte le aziende" value={values.company} onChange={(value) => {
          setValues((current) => ({ ...current, company: value, location: 'all', department: 'all', employee: 'all' }));
        }} options={filters.aziende.map((item) => ({ value: item.id, label: item.nome }))} disabled={loading} />
        <SelectFilter id="ferie-analisi-location" label="Tutti i locali" value={values.location} onChange={(value) => {
          setValues((current) => ({ ...current, location: value, department: 'all', employee: 'all' }));
        }} options={locations} disabled={loading || !filters.locali.length} />
        <SelectFilter id="ferie-analisi-department" label="Tutti i reparti" value={values.department} onChange={(value) => {
          setValues((current) => ({ ...current, department: value, employee: 'all' }));
        }} options={departments} disabled={loading || !filters.reparti.length} />
        <SelectFilter id="ferie-analisi-employee" label="Tutti i dipendenti" value={values.employee} onChange={(value) => set('employee', value)} options={employees} disabled={loading || !filters.dipendenti.length} />
        <SelectFilter id="ferie-analisi-state" label="Tutti gli stati" value={values.state} onChange={(value) => set('state', value)} options={STATI.map(([value, label]) => ({ value, label }))} disabled={loading} />
        </div>
        <div className="mt-3 hidden items-center gap-2 text-xs text-stone-500 md:flex">
          <Filter className="h-3.5 w-3.5" /> I risultati rispettano sempre il perimetro autorizzato dal server.
        </div>
      </div>
    </section>
  );
}

export const INSIGHT_ITEMS = [
  { key: 'richieste', to: '/ferie/richieste', icon: ClipboardEdit, title: 'Richieste', description: 'Gestisci le richieste ferie dei dipendenti.' },
  { key: 'locali', to: '/ferie/situazione-locale', icon: Building2, title: 'Situazione per locale', description: 'Confronta saldi e reparti dei locali autorizzati.' },
  { key: 'dipendenti', to: '/ferie/situazione-dipendente', icon: UserCircle, title: 'Situazione per dipendente', description: 'Apri la lista dettagliata delle persone.' },
  { key: 'mensile', to: '/ferie/andamento-mensile', icon: TrendingUp, title: 'Andamento mensile', description: 'Leggi maturato, goduto e programmato mese per mese.' },
  { key: 'pianificazione', to: '/ferie/pianificazione', icon: CalendarDays, title: 'Pianificazione annuale', description: 'Organizza e consulta la pianificazione delle ferie durante l’anno.' },
  { key: 'storico', to: '/ferie/storico', icon: History, title: 'Storico', description: 'Consulta lo storico delle ferie e delle operazioni.' },
];

export function InsightAccessCards({ year }) {
  return (
    <section className="space-y-3" data-testid="ferie-analisi-access">
      <div>
        <h2 className="font-display text-lg font-semibold tracking-tight text-brand-navy">Approfondimenti</h2>
        <p className="mt-1 text-sm text-stone-500">Apri solo la vista che ti serve, senza appesantire la schermata principale.</p>
      </div>
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
        {INSIGHT_ITEMS.map(({ key, to, icon: Icon, title, description }) => (
          <Link
            key={key}
            to={`${to}?anno=${year}`}
            data-testid={`ferie-analisi-access-${key}`}
            className="group flex min-h-[76px] items-center gap-3 rounded-2xl border border-app-border bg-app-surface p-4 text-left transition-all hover:-translate-y-0.5 hover:border-brand-green hover:shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-300 focus-visible:ring-offset-2"
          >
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-emerald-50 text-brand-green"><Icon className="h-5 w-5" aria-hidden="true" /></span>
            <span className="min-w-0 flex-1"><span className="block font-display text-sm font-semibold leading-tight text-brand-navy">{title}</span><span className="mt-1 block text-xs leading-relaxed text-stone-500">{description}</span></span>
            <ChevronRight className="h-4 w-4 shrink-0 text-stone-300 transition-transform group-hover:translate-x-0.5 group-hover:text-brand-green" aria-hidden="true" />
          </Link>
        ))}
      </div>
    </section>
  );
}

export function MonthlyView({ rows, baseline, year }) {
  const monthlyRows = rows || [];
  const baselineDate = baseline?.data ? new Date(`${baseline.data}T00:00:00`) : null;
  const baselineMonth = baselineDate && baselineDate.getFullYear() === Number(year)
    ? baselineDate.getMonth() + 1
    : null;
  const hasBaseline = baselineMonth !== null && baseline?.saldo !== null && baseline?.saldo !== undefined;
  const currentYear = new Date().getFullYear();
  const referenceMonth = Number(year) === currentYear ? new Date().getMonth() + 1 : 12;
  const monthlyAccrual = (row) => {
    const previousRow = monthlyRows.find((candidate) => candidate.mese === row.mese - 1);
    return Math.max(0, Number(row.maturato || 0) - Number(previousRow?.maturato || 0));
  };
  const referenceRows = monthlyRows.filter((row) => (
    (!hasBaseline || row.mese > baselineMonth) && row.mese <= referenceMonth
  ));
  const projectedRows = monthlyRows.filter((row) => row.mese > referenceMonth);
  const referenceAccrual = referenceRows.reduce((total, row) => total + monthlyAccrual(row), 0);
  const projectedAccrual = projectedRows.reduce((total, row) => total + monthlyAccrual(row), 0);
  const yearEndRow = monthlyRows.find((row) => row.mese === 12);

  return (
    <section className="rounded-2xl border border-app-border bg-app-surface p-4 sm:p-5" data-testid="ferie-analisi-monthly">
      <div className="mb-3 flex items-center gap-2">
        <TrendingUp className="h-4 w-4 text-brand-green" />
        <div>
          <h2 className="font-display font-semibold tracking-tight text-brand-navy">Maturazione mese per mese · {year}</h2>
          <p className="text-xs text-stone-500">Dati di riferimento e maturazione prevista fino al 31/12.</p>
        </div>
      </div>
      <div className="mb-3 flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-stone-500" aria-label="Legenda maturazione">
        <span className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-stone-300" />Baseline</span>
        <span className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-brand-green" />Maturato di riferimento</span>
        <span className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-blue-400" />Maturazione prevista</span>
      </div>
      <div className="grid grid-cols-4 gap-2 sm:grid-cols-6 lg:grid-cols-12" data-testid="ferie-analisi-month-grid">
        {monthlyRows.map((row) => (
          (() => {
            const inBaseline = hasBaseline && row.mese <= baselineMonth;
            const projected = !inBaseline && row.mese > referenceMonth;
            const displayedAccrual = monthlyAccrual(row);
            const state = inBaseline ? 'baseline' : projected ? 'prevista' : 'riferimento';
            return (
              <article
                key={row.mese}
                data-testid={`ferie-analisi-month-${row.mese}`}
                data-baseline={inBaseline ? 'true' : 'false'}
                data-state={state}
                className={`min-w-0 rounded-xl border px-1.5 py-2 text-center sm:px-2 ${
                  inBaseline ? 'border-stone-200 bg-stone-50/60' : projected ? 'border-blue-100 bg-blue-50/60' : 'border-emerald-100 bg-emerald-50/50'
                }`}
              >
                <div className="truncate text-[9px] font-bold uppercase tracking-[0.12em] text-stone-500">{row.label || MESI[row.mese - 1]}</div>
                {inBaseline ? (
                  <>
                    <div className="mt-1 text-sm font-semibold leading-none text-stone-400">—</div>
                    <div className="mt-1 text-[9px] leading-tight text-stone-400">baseline</div>
                  </>
                ) : (
                  <>
                    <div className={`mt-1 text-sm font-semibold leading-none ${projected ? 'text-blue-700' : 'text-brand-green'}`}><span aria-hidden="true">+</span>{fmt(displayedAccrual)}<span className="ml-0.5 text-[10px] font-normal">g</span></div>
                    <div className={`mt-1 text-[9px] leading-tight ${projected ? 'text-blue-700' : 'text-emerald-700'}`}>{projected ? 'maturazione prevista' : 'maturato di riferimento'}</div>
                  </>
                )}
                <div className="mt-1 flex justify-center gap-1.5 text-[9px] leading-tight text-stone-500">
                  <span>G {fmt(row.goduto)}g</span>
                  <span className="text-blue-600">P {fmt(row.programmato)}g</span>
                </div>
              </article>
            );
          })()
        ))}
      </div>
      <div className="mt-3 flex flex-wrap items-center justify-between gap-x-3 gap-y-1 border-t border-stone-100 pt-3 text-xs text-stone-500" data-testid="ferie-analisi-monthly-summary">
        {hasBaseline && <span>Saldo alla baseline: <strong className="font-semibold text-stone-700">{fmt(baseline.saldo)}g</strong></span>}
        <span>Maturato di riferimento: <strong className="font-semibold text-brand-green">+{fmt(referenceAccrual)}g</strong></span>
        <span>Maturazione prevista: <strong className="font-semibold text-blue-700">+{fmt(projectedAccrual)}g</strong></span>
        {yearEndRow && <span>{Number(year) === currentYear ? 'Saldo previsto' : 'Saldo'} al 31/12: <strong className="font-semibold text-brand-green">{fmt(yearEndRow.disponibile)}g</strong></span>}
      </div>
    </section>
  );
}

export function BalanceFormula({ employee, year, canEdit = false, onSaved }) {
  return (
    <BalanceExplanation
      balance={{
        riportoPrecedente: employee?.riportato,
        maturate: employee?.maturato,
        godute: employee?.goduto,
        programmate: employee?.programmato,
        rettifiche: employee?.rettifiche,
        disponibile: employee?.disponibile,
         quote: employee?.quote,
         riportoDettaglio: employee?.riportoDettaglio,
      }}
      baseline={employee?.baseline}
      year={year}
      projection={Number(year) >= new Date().getFullYear()}
      policy={employee?.policy}
      canEdit={canEdit}
      onSaved={onSaved}
      testid="ferie-analisi-balance-formula"
    />
  );
}

function CollapsibleSection({ title, description, icon: Icon, children, testid, defaultOpen = true }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <section className="overflow-hidden rounded-2xl border border-app-border bg-app-surface" data-testid={testid}>
      <button
        type="button"
        className="flex w-full items-center justify-between gap-3 px-5 py-4 text-left hover:bg-stone-50"
        onClick={() => setOpen((current) => !current)}
        aria-expanded={open}
      >
        <span className="flex min-w-0 items-center gap-2">
          <Icon className="h-4 w-4 shrink-0 text-brand-green" />
          <span><span className="block font-display font-semibold tracking-tight text-brand-navy">{title}</span><span className="mt-0.5 block text-xs text-stone-500">{description}</span></span>
        </span>
        <ChevronDown className={`h-4 w-4 shrink-0 text-stone-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && <div className="border-t border-stone-100 p-4 sm:p-5">{children}</div>}
    </section>
  );
}

function PeriodRow({ period, employeeName, canEdit, onEdit, onDelete }) {
  return (
    <div className="flex flex-col gap-2 border-b border-stone-100 px-4 py-3 last:border-0 sm:flex-row sm:items-center sm:justify-between" data-testid={`analisi-periodo-${period.id}`}>
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2 text-sm font-medium text-stone-900">
          {employeeName && <span className="truncate">{employeeName}</span>}
          <span className="text-stone-500">{period.dal} → {period.al}</span>
          <span className="text-xs text-stone-500">· {fmt(period.giorni)}g</span>
        </div>
        {period.note && <div className="mt-0.5 truncate text-xs italic text-stone-500">&ldquo;{period.note}&rdquo;</div>}
      </div>
      <div className="flex items-center gap-2">
        <StateBadge value={period.stato} />
        {canEdit && <Button type="button" size="sm" variant="outline" className="rounded-full gap-1" onClick={() => onEdit(period)} data-testid={`edit-analisi-periodo-${period.id}`}><ClipboardEdit className="h-3.5 w-3.5" /> Modifica</Button>}
        {canEdit && <Button type="button" size="sm" variant="ghost" className="rounded-full gap-1 text-red-700 hover:bg-red-50" onClick={() => onDelete(period)} data-testid={`delete-analisi-periodo-${period.id}`}><Trash2 className="h-3.5 w-3.5" /> Elimina</Button>}
      </div>
    </div>
  );
}

export function PeriodEditor({ employeeId, period, year, onClose, onSaved, employees = [], allowEmployeeChange = false, defaultState = 'richiesta' }) {
  const [form, setForm] = useState({
    dipendente_id: period?.dipendenteId || employeeId || '', dal: period?.dal || `${year}-01-01`, al: period?.al || `${year}-01-01`,
    stato: period?.stato || defaultState, anno_competenza: period?.annoCompetenza || year, note: period?.note || '',
  });
  const [busy, setBusy] = useState(false);
  const set = (key, value) => setForm((current) => ({ ...current, [key]: value }));
  const save = async () => {
    if (!form.dal || !form.al || form.al < form.dal) return toast.error('Controlla l’intervallo date');
    setBusy(true);
    try {
      const path = period ? `/api/ferie/analisi/periodi/${period.id}` : '/api/ferie/analisi/periodi';
      const body = period
        ? { dal: form.dal, al: form.al, stato: form.stato, anno_competenza: Number(form.anno_competenza), note: form.note || null }
        : { ...form, anno_competenza: Number(form.anno_competenza), note: form.note || null };
      if (period && form.dipendente_id !== period.dipendenteId) body.dipendente_id = form.dipendente_id;
      await apiFetch(path, { method: period ? 'PATCH' : 'POST', body: JSON.stringify(body) });
      toast.success(period ? 'Periodo aggiornato' : 'Periodo inserito');
      onSaved();
    } catch (error) {
      toast.error(error.message || 'Salvataggio non riuscito');
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal title={period ? 'Modifica periodo ferie' : 'Inserisci periodo ferie'} onClose={onClose}>
      <div className="space-y-3">
        {(allowEmployeeChange || !period) && <label className="block text-xs font-semibold text-stone-600">Dipendente
          <select value={form.dipendente_id} onChange={(event) => set('dipendente_id', event.target.value)} className="mt-1 h-10 w-full rounded-xl border border-app-border-strong bg-app-surface px-3 text-sm" disabled={!employees.length}>
            {!employees.length && <option value={form.dipendente_id}>{form.dipendente_id || 'Nessun dipendente disponibile'}</option>}
            {employees.map((employee) => <option key={employee.id} value={employee.id}>{employee.displayName || `${employee.nome} ${employee.cognome}`}</option>)}
          </select>
        </label>}
        <div className="grid grid-cols-2 gap-3">
          <label className="text-xs font-semibold text-stone-600">Dal<Input type="date" value={form.dal} onChange={(event) => set('dal', event.target.value)} className="mt-1" /></label>
          <label className="text-xs font-semibold text-stone-600">Al<Input type="date" value={form.al} onChange={(event) => set('al', event.target.value)} className="mt-1" /></label>
        </div>
        <label className="block text-xs font-semibold text-stone-600">Stato
          <select value={form.stato} onChange={(event) => set('stato', event.target.value)} className="mt-1 h-10 w-full rounded-xl border border-app-border-strong bg-app-surface px-3 text-sm">
            {STATI.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
          </select>
        </label>
        <label className="block text-xs font-semibold text-stone-600">Note
          <textarea value={form.note} onChange={(event) => set('note', event.target.value)} rows={3} className="mt-1 w-full rounded-xl border border-app-border-strong bg-app-surface p-3 text-sm" />
        </label>
      </div>
      <div className="mt-5 flex justify-end gap-2">
        <Button variant="ghost" onClick={onClose}>Annulla</Button>
        <Button onClick={save} disabled={busy} className="rounded-full bg-brand-green text-white hover:bg-brand-green-hover"><Save className="mr-1.5 h-4 w-4" />{busy ? 'Salvataggio…' : 'Salva'}</Button>
      </div>
    </Modal>
  );
}

function AdjustmentEditor({ employee, year, onSaved }) {
  const [type, setType] = useState('maturato');
  const [value, setValue] = useState(String(employee?.rettificheDettaglio?.maturato || 0));
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await apiFetch(`/api/ferie/analisi/${employee.id}/rettifiche/${year}/${type}`, {
        method: 'PUT', body: JSON.stringify({ valore: Number(value), note: note || null }),
      });
      toast.success('Rettifica salvata');
      onSaved();
    } catch (error) {
      toast.error(error.message || 'Rettifica non salvata');
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="rounded-2xl border border-amber-200 bg-amber-50/60 p-4" data-testid="ferie-analisi-adjustment">
      <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-amber-950"><ClipboardEdit className="h-4 w-4" /> Rettifica amministrativa</div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <label className="text-xs font-semibold text-stone-600">Voce
          <select value={type} onChange={(event) => { setType(event.target.value); setValue(String(employee?.rettificheDettaglio?.[event.target.value] || 0)); }} className="mt-1 h-10 w-full rounded-xl border border-amber-200 bg-white px-3 text-sm">
            <option value="maturato">Maturato</option><option value="riporto">Riporto</option><option value="goduto">Goduto</option>
          </select>
        </label>
        <label className="text-xs font-semibold text-stone-600">Giorni (+ / −)<Input type="number" step="0.01" value={value} onChange={(event) => setValue(event.target.value)} className="mt-1 bg-white" /></label>
        <label className="text-xs font-semibold text-stone-600">Nota<Input value={note} onChange={(event) => setNote(event.target.value)} placeholder="Motivo della rettifica" className="mt-1 bg-white" /></label>
      </div>
      <div className="mt-3 flex justify-end"><Button onClick={save} disabled={busy} size="sm" className="rounded-full bg-stone-900 text-white hover:bg-stone-800"><Save className="mr-1.5 h-3.5 w-3.5" />{busy ? 'Salvataggio…' : 'Salva rettifica'}</Button></div>
    </div>
  );
}

function Modal({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" role="dialog" aria-modal="true" onClick={onClose}>
      <div className="w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-2xl border border-app-border bg-app-surface p-5 shadow-xl sm:p-6" onClick={(event) => event.stopPropagation()}>
        <div className="mb-4 flex items-center justify-between gap-3"><h2 className="font-display text-lg font-semibold text-brand-navy">{title}</h2><button type="button" onClick={onClose} aria-label="Chiudi" className="rounded-full px-3 py-1 text-xl text-stone-400 hover:bg-stone-100">×</button></div>
        {children}
      </div>
    </div>
  );
}

function EmployeeTable({ rows, year }) {
  return (
    <section className="rounded-2xl border border-app-border bg-app-surface overflow-hidden" data-testid="ferie-analisi-employees">
      <div className="flex items-center justify-between border-b border-stone-100 px-5 py-4">
        <div><h2 className="font-display font-semibold tracking-tight text-brand-navy">Situazione per dipendente</h2><p className="text-xs text-stone-500">{rows.length} persone nel perimetro selezionato</p></div>
        <Search className="h-4 w-4 text-stone-400" />
      </div>
      <div className="divide-y divide-stone-100">
        {rows.length === 0 ? <div className="p-8 text-center text-sm text-stone-500">Nessun dipendente con questi filtri.</div> : rows.map((row) => (
          <Link key={row.id} to={`/ferie/analisi/${row.id}?anno=${year}`} className="group flex flex-col gap-3 px-5 py-4 transition-colors hover:bg-stone-50 sm:flex-row sm:items-center sm:justify-between" data-testid={`analisi-dipendente-${row.id}`}>
            <div className="min-w-0"><div className="truncate font-display font-semibold text-brand-navy">{row.displayName}</div><div className="mt-0.5 truncate text-xs text-stone-500">{row.aziendaNome || 'Azienda'} · {row.localeNome || 'Locale'} · {row.reparto || 'Reparto n/d'}</div></div>
            <div className="grid grid-cols-4 gap-3 text-right sm:min-w-[390px]"><TinyMetric label="Maturato" value={row.maturato} /><TinyMetric label="Goduto" value={row.goduto} /><TinyMetric label="Prog." value={row.programmato} /><TinyMetric label="Disp." value={row.disponibile} good /></div>
            <ChevronRight className="hidden h-4 w-4 text-stone-300 sm:block" />
          </Link>
        ))}
      </div>
    </section>
  );
}

function TinyMetric({ label, value, good }) {
  return <div><div className={`font-display text-sm font-semibold ${good ? 'text-brand-green' : 'text-stone-800'}`}>{fmt(value)}g</div><div className="text-[9px] font-bold uppercase tracking-widest text-stone-400">{label}</div></div>;
}

function LocationsTable({ rows, employees, year }) {
  return (
    <section className="rounded-2xl border border-app-border bg-app-surface overflow-hidden" data-testid="ferie-analisi-locations">
      <div className="border-b border-stone-100 px-5 py-4"><h2 className="font-display font-semibold tracking-tight text-brand-navy">Situazione per locale</h2><p className="text-xs text-stone-500">Confronto dei saldi nel perimetro selezionato.</p></div>
      <div className="grid grid-cols-1 gap-3 p-4 md:grid-cols-2">
        {rows.length === 0 ? <div className="p-5 text-sm text-stone-500">Nessun locale disponibile.</div> : rows.map((row) => {
          const departments = employees.filter((employee) => employee.localeId === row.id).reduce((groups, employee) => {
            const key = employee.repartoId || 'senza-reparto';
            const group = groups[key] || { id: employee.repartoId, nome: employee.reparto || 'Reparto n/d', count: 0 };
            group.count += 1; groups[key] = group; return groups;
          }, {});
          const content = (
            <div className="rounded-xl border border-stone-200 bg-stone-50/50 p-4 transition-colors hover:border-brand-green hover:bg-emerald-50/30">
              {row.id ? <div className="flex items-center justify-between gap-2"><span className="font-display font-semibold text-stone-900">{row.nome}</span><span className="text-xs text-stone-500">{row.dipendenti} dip.</span></div> : <div className="flex items-center justify-between gap-2"><span className="font-display font-semibold text-stone-900">{row.nome}</span><span className="text-xs text-stone-500">{row.dipendenti} dip.</span></div>}
              <div className="mt-3 grid grid-cols-3 gap-2"><TinyMetric label="Maturato" value={row.maturato} /><TinyMetric label="Goduto" value={row.goduto} /><TinyMetric label="Disponibile" value={row.disponibile} good /></div>
                {Object.keys(departments).length > 0 && <div className="mt-3 border-t border-stone-200 pt-3"><div className="mb-1 text-[9px] font-bold uppercase tracking-widest text-stone-400">Reparti · apri dettaglio</div><div className="flex flex-wrap gap-1.5">{Object.values(departments).map((department) => department.id ? <Link key={department.id} to={`/ferie/situazione-dipendente?anno=${year}&locale_id=${row.id}&reparto_id=${department.id}`} className="rounded-full bg-white px-2.5 py-1 text-[11px] text-brand-blue hover:bg-emerald-100">{department.nome} · {department.count}</Link> : <span key={department.nome} className="rounded-full bg-white px-2 py-1 text-[11px] text-stone-600">{department.nome} · {department.count}</span>)}</div></div>}
                {row.id && <Link data-testid={`analisi-locale-${row.id}`} to={`/ferie/situazione-locale/${row.id}?anno=${year}`} className="mt-3 flex items-center justify-end gap-1 text-xs font-semibold text-brand-blue">Apri locale <ChevronRight className="h-3.5 w-3.5" /></Link>}
            </div>
          );
          return <div key={row.id || row.nome}>{content}</div>;
        })}
      </div>
    </section>
  );
}

function Timeline({ periods, employeesById, canEdit, onEdit, onDelete }) {
  return (
    <section className="rounded-2xl border border-app-border bg-app-surface overflow-hidden" data-testid="ferie-analisi-timeline">
      <div className="flex items-center gap-2 border-b border-stone-100 px-5 py-4"><CalendarDays className="h-4 w-4 text-brand-green" /><div><h2 className="font-display font-semibold tracking-tight text-brand-navy">Timeline annuale</h2><p className="text-xs text-stone-500">Periodi nel perimetro e nello stato selezionato.</p></div></div>
      {periods.length === 0 ? <div className="p-8 text-center text-sm text-stone-500">Nessun periodo registrato per questa selezione.</div> : <div className="divide-y divide-stone-100">{periods.map((period) => <PeriodRow key={period.id} period={period} employeeName={employeesById[period.dipendenteId]?.displayName} canEdit={canEdit} onEdit={onEdit} onDelete={onDelete} />)}</div>}
    </section>
  );
}

export default function FerieAnalisi({ embedded = false, refreshKey = 0 }) {
  const { legacyId } = useParams();
  const location = useLocation();
  const me = useEffectiveMe();
  const canEdit = hasCap(me, 'ferie.balance.manage') || hasCap(me, 'ferie.config.manage');
  const canAdjustQuota = hasCap(me, 'ferie.config.manage');
  const currentYear = new Date().getFullYear();
  const requestedYear = Number(new URLSearchParams(location.search).get('anno'));
  const initialYear = requestedYear >= 2000 && requestedYear <= 2200 ? requestedYear : currentYear;
  const initialParams = new URLSearchParams(location.search);
  const [values, setValues] = useState({
    year: initialYear,
    company: initialParams.get('azienda_id') || 'all',
    location: initialParams.get('locale_id') || 'all',
    department: initialParams.get('reparto_id') || 'all',
    employee: initialParams.get('dipendente_id') || 'all',
    state: initialParams.get('stato') || 'all',
  });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editingPeriod, setEditingPeriod] = useState(null);
  const [addingPeriod, setAddingPeriod] = useState(false);
  const filters = data?.filters || EMPTY_FILTERS;

  useEffect(() => {
    if (legacyId) return;
    const params = new URLSearchParams(location.search);
    const queryYear = Number(params.get('anno'));
    const nextValues = {
      year: queryYear >= 2000 && queryYear <= 2200 ? queryYear : initialYear,
      company: params.get('azienda_id') || 'all',
      location: params.get('locale_id') || 'all',
      department: params.get('reparto_id') || 'all',
      employee: params.get('dipendente_id') || 'all',
      state: params.get('stato') || 'all',
    };
    setValues((current) => Object.keys(nextValues).every((key) => current[key] === nextValues[key]) ? current : nextValues);
  }, [initialYear, legacyId, location.search]);

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const params = new URLSearchParams({ anno: String(values.year) });
      if (!legacyId) {
        if (values.company !== 'all') params.set('azienda_id', values.company);
        if (values.location !== 'all') params.set('locale_id', values.location);
        if (values.department !== 'all') params.set('reparto_id', values.department);
        if (values.employee !== 'all') params.set('dipendente_id', values.employee);
        if (values.state !== 'all') params.set('stato', values.state);
      }
      const path = legacyId ? `/api/ferie/analisi/${encodeURIComponent(legacyId)}?${params}` : `/api/ferie/analisi?${params}`;
      setData(await apiFetch(path));
    } catch (requestError) {
      setError(requestError.message || 'Analisi non disponibile');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [legacyId, values]);
  useEffect(() => { load(); }, [load, refreshKey]);

  const employeeRows = useMemo(() => data?.perDipendente || [], [data]);
  const selectedEmployee = employeeRows[0];
  const employeesById = useMemo(() => Object.fromEntries(employeeRows.map((row) => [row.id, row])), [employeeRows]);
  const kpi = data?.kpi || {};

  const reload = () => load();
  const onPeriodSaved = () => { setEditingPeriod(null); setAddingPeriod(false); load(); };
  const onPeriodDelete = async (period) => {
    if (!window.confirm(`Eliminare il periodo ferie di ${employeesById[period.dipendenteId]?.displayName || 'questo dipendente'}?`)) return;
    try {
      await apiFetch(`/api/ferie/analisi/periodi/${period.id}`, { method: 'DELETE' });
      toast.success('Periodo eliminato');
      setEditingPeriod(null);
      await load();
    } catch (error) {
      toast.error(error.message || 'Eliminazione non riuscita');
    }
  };

  if (legacyId) {
    return (
      <div className="space-y-6 pb-20 md:pb-0" data-testid="ferie-analisi-detail">
        <Link to={`/ferie/situazione-dipendente?anno=${data?.anno || currentYear}`} className="flex items-center gap-1 text-sm text-stone-500 hover:text-stone-900" data-testid="ferie-analisi-back-employees"><ArrowLeft className="h-4 w-4" /> Situazione per dipendente</Link>
        {loading && <Loading />}
        {error && <ErrorState message={error} retry={load} />}
        {!loading && !error && selectedEmployee && (
          <>
            <header className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div><div className="text-xs font-bold uppercase tracking-[0.2em] text-brand-green">Dettaglio dipendente · {data.anno}</div><h1 className="mt-1 font-display text-3xl font-semibold tracking-tight text-brand-navy">{selectedEmployee.displayName}</h1><p className="mt-1 text-sm text-stone-500">{selectedEmployee.aziendaNome || 'Azienda'} · {selectedEmployee.localeNome || 'Locale'} · {selectedEmployee.reparto || 'Reparto n/d'} · Inizio: {selectedEmployee.dataInizio || 'n/d'}</p></div>
              {canEdit && <Button type="button" onClick={() => setAddingPeriod(true)} className="rounded-full bg-brand-green text-white hover:bg-brand-green-hover"><Plus className="mr-1.5 h-4 w-4" /> Inserisci periodo</Button>}
            </header>
             <YearNotice meta={data.metaAnno || data.meta} />
             <div className="grid grid-cols-2 gap-3 md:grid-cols-6">
               <Metric
                 label={data.anno >= currentYear ? 'Maturazione prevista' : 'Maturato'}
                 value={selectedEmployee.maturato}
                 hint={data.anno >= currentYear ? `Proiezione al 31/12/${data.anno}` : 'Maturazione registrata nell’anno'}
               />
               <Metric label="Riportato" value={selectedEmployee.riportato} />
               <Metric label="Goduto" value={selectedEmployee.goduto} />
               <Metric label="Programmato" value={selectedEmployee.programmato} tone="warn" />
               <Metric label="Rettifiche" value={selectedEmployee.rettifiche} tone="info" />
               <Metric label="Disponibile" displayLabel="GIORNI DISPONIBILI" value={selectedEmployee.disponibile} tone="good" highlight hint={data.anno >= currentYear ? `Proiezione al 31/12/${data.anno}` : 'Saldo dell’anno selezionato'} />
             </div>
             <BalanceFormula employee={selectedEmployee} year={data.anno} canEdit={canAdjustQuota} onSaved={reload} />
            {canEdit && <AdjustmentEditor employee={selectedEmployee} year={data.anno} onSaved={reload} />}
             <MonthlyView rows={selectedEmployee.mensile} baseline={selectedEmployee.baseline} year={data.anno} />
             <Timeline periods={data.periodi || []} employeesById={employeesById} canEdit={canEdit} onEdit={setEditingPeriod} onDelete={onPeriodDelete} />
            <YearHistory years={data.filters?.anni || []} currentYear={data.anno} employeeId={selectedEmployee.id} />
            <ChangeHistory records={data.storicoModifiche || []} />
             {addingPeriod && <PeriodEditor employeeId={selectedEmployee.id} year={data.anno} employees={filters.dipendenti} allowEmployeeChange onClose={() => setAddingPeriod(false)} onSaved={onPeriodSaved} />}
             {editingPeriod && <PeriodEditor employeeId={selectedEmployee.id} period={editingPeriod} year={data.anno} employees={filters.dipendenti} allowEmployeeChange onClose={() => setEditingPeriod(null)} onSaved={onPeriodSaved} />}
          </>
        )}
        {!loading && !error && !selectedEmployee && <ErrorState message="Dipendente non trovato nel perimetro autorizzato." />}
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-20 md:pb-0" data-testid={embedded ? 'ferie-analisi-embedded' : 'ferie-analisi-page'}>
      {!embedded && <header className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div><div className="text-xs font-bold uppercase tracking-[0.2em] text-brand-green">Ferie · controllo annuale</div><h1 className="mt-1 font-display text-3xl font-semibold tracking-tight text-brand-navy md:text-4xl">Analisi ferie annuale</h1><p className="mt-2 text-sm text-stone-500">Saldo, periodi e maturazione nel perimetro autorizzato.</p></div>
        <div className="flex gap-2"><Link to="/ferie" className="inline-flex items-center rounded-full border border-app-border bg-app-surface px-4 py-2 text-sm font-semibold text-stone-700 hover:border-brand-green"><ArrowLeft className="mr-1.5 h-4 w-4" /> Gestione ferie</Link><Button type="button" variant="outline" onClick={reload} disabled={loading} className="rounded-full" data-testid="ferie-analisi-refresh"><RefreshCw className={`mr-1.5 h-4 w-4 ${loading ? 'animate-spin' : ''}`} /> Aggiorna</Button></div>
       </header>}
      <FilterPanel filters={filters} values={values} setValues={setValues} loading={loading} />
      {loading && <Loading />}
      {error && <ErrorState message={error} retry={load} />}
      {!loading && !error && data && (
        <>
           <YearNotice meta={data.metaAnno || data.meta} />
           <div id="ferie-sintesi-principale" data-testid="ferie-sintesi-principale">
             <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6"><Metric label="Maturato" value={kpi.maturato} /><Metric label="Riportato" value={kpi.riportato} /><Metric label="Goduto" value={kpi.goduto} /><Metric label="Programmato" value={kpi.programmato} tone="warn" /><Metric label="Rettifiche" value={kpi.rettifiche} tone="info" /><Metric label="Disponibile" displayLabel="GIORNI DISPONIBILI" value={kpi.disponibile} tone="good" highlight unit="" hint={`Saldo disponibile al 31/12/${data.anno}`} /></div>
           </div>
            <InsightAccessCards year={data.anno} />
          {editingPeriod && <PeriodEditor employeeId={editingPeriod.dipendenteId} period={editingPeriod} year={data.anno} onClose={() => setEditingPeriod(null)} onSaved={onPeriodSaved} />}
        </>
      )}
    </div>
  );
}

function YearHistory({ years, currentYear, employeeId }) {
  const history = years.filter((year) => year !== currentYear);
  return (
    <section className="rounded-2xl border border-app-border bg-app-surface p-5" data-testid="ferie-analisi-year-history">
      <div className="mb-3 flex items-center gap-2"><CalendarDays className="h-4 w-4 text-brand-green" /><div><h2 className="font-display font-semibold tracking-tight text-brand-navy">Storico anni</h2><p className="text-xs text-stone-500">Anni disponibili per questo dipendente.</p></div></div>
      <div className="flex flex-wrap gap-2">{history.length === 0 ? <span className="text-sm text-stone-500">Nessun anno precedente disponibile.</span> : history.map((year) => <Link key={year} to={`/ferie/analisi/${employeeId}?anno=${year}`} className="rounded-full border border-stone-200 px-3 py-1.5 text-sm font-semibold text-brand-blue hover:border-brand-green hover:bg-emerald-50">{year}</Link>)}</div>
    </section>
  );
}

function ChangeHistory({ records }) {
  return (
    <section className="rounded-2xl border border-app-border bg-app-surface overflow-hidden" data-testid="ferie-analisi-history">
      <div className="flex items-center gap-2 border-b border-stone-100 px-5 py-4"><History className="h-4 w-4 text-brand-green" /><div><h2 className="font-display font-semibold tracking-tight text-brand-navy">Storico modifiche amministrative</h2><p className="text-xs text-stone-500">Eventi registrati per questo dipendente e anno.</p></div></div>
      {records.length === 0 ? <div className="p-8 text-center text-sm text-stone-500">Nessuna modifica amministrativa registrata.</div> : <div className="divide-y divide-stone-100">{records.map((record) => <div key={record.id} className="flex flex-col gap-1 px-5 py-3 text-sm sm:flex-row sm:items-center sm:justify-between"><div><span className="font-semibold text-stone-800">{record.azione}</span><span className="ml-2 text-xs text-stone-500">{record.entita}</span></div><div className="text-xs text-stone-500">{record.autore || 'Utente autorizzato'} · {record.data ? new Date(record.data).toLocaleString('it-IT') : '—'}</div></div>)}</div>}
    </section>
  );
}

function Loading() {
  return <div className="flex items-center gap-2 text-sm text-stone-500" data-testid="ferie-analisi-loading"><Loader2 className="h-4 w-4 animate-spin" /> Caricamento analisi…</div>;
}

function ErrorState({ message, retry }) {
  return <div className="rounded-2xl border border-red-200 bg-red-50 p-6 text-center text-sm text-red-800" role="alert"><div>{message}</div>{retry && <Button type="button" variant="outline" onClick={retry} className="mt-3 rounded-full border-red-200 text-red-800">Riprova</Button>}</div>;
}